import type {
  LocationType,
  SettleDestinationResponse,
  StockTransferRoute,
  TransferChainNode,
  TransferDestination,
  TransferStatus,
} from '@cuito/types';
import { apiClient } from '../lib/apiClient';
import type { Paginated } from './catalog';
import { locationsApi } from './locations';

/** 库存项列表的一行（`GET /stock/items`） */
export interface StockItemView {
  id: string;
  locationId: string;
  productId: string;
  productName: string;
  ownerTenantId: string;
  availableQty: number;
  reservedQty: number;
  soldQty: number;
  /** 已退回数量（退款回补），列表页不展示但出网带回来 */
  returnedQty: number;
  status: string;
}

/** `GET /stock/summary` 的一行：按地点聚合的库存概览 */
export interface StockSummaryRow {
  locationId: string;
  locationName: string;
  type: string;
  itemKindCount: number;
  totalAvailableQty: number;
  totalReservedQty: number;
  totalSoldQty: number;
}

/** 仓库列表的一行（`GET /warehouses`） */
export interface WarehouseView {
  id: string;
  name: string;
  address: string | null;
  note: string | null;
  location: { id: string; code: string; type: string } | null;
  itemKindCount: number;
  totalAvailableQty: number;
  createdAt: string;
}

/**
 * 地点（`GET /locations`）。
 *
 * `type` 用 `@cuito/types` 的 `LocationType` 而不是就地写一遍字面量联合：
 * 枚举早已存在（`LOCATION_TYPES`），重写一份就会漂移（计划全局规则）。
 */
export interface LocationView {
  id: string;
  name: string;
  code: string;
  type: LocationType;
  active: boolean;
  shelfRows: number;
  shelfCols: number;
  createdAt: string;
}

/** 调拨单里的一行 */
export interface TransferLineView {
  id: string;
  productId: string;
  quantity: number;
  receivedQty: number;
  ownerTenantId: string;
}

/** 调拨单视图（`04-api-contract.md` §9.2 / §9.3） */
export interface TransferView {
  id: string;
  code: string;
  route: StockTransferRoute;
  status: TransferStatus;
  transferDestination: string | null;
  parentTransferId: string | null;
  note: string | null;
  shippedAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  sourceLocation: { id: string; name: string };
  targetLocation: { id: string; name: string };
  lines: TransferLineView[];
}

/**
 * `@cuito/types` 的冻结导出（链 A Task 1）：契约 §9.4/§9.5 的响应形状**只有这一处实现**。
 * 本层不重抄 —— 重抄的那一份不会有任何编译错误。
 */
export type { SettleDestinationResponse, TransferChainNode } from '@cuito/types';

/** `POST /stock/adjust` 的结果（契约 §9.1） */
export interface StockAdjustResult {
  stockItemId: string;
  availableQty: number;
  reservedQty: number;
  soldQty: number;
}

/**
 * 仓库、库存与调拨的 API 调用（`04-api-contract.md` §6.2、§7、§9）。
 *
 * **两处响应的包裹形状不同，不要照抄**（契约 §9 的行内说明）：
 *   - `GET /stock/items`、`GET /stock/summary`、`GET /warehouses`、`GET /locations`
 *     是**裸数组**——量级由「社团 × 地点 × 商品」决定，翻页只会让前端多写一层循环；
 *   - `GET /stock/transfers` 是契约 §1.3 的**五字段分页包裹**。
 */
export const stockApi = {
  listWarehouses: () => apiClient.get<WarehouseView[]>('/warehouses'),
  createWarehouse: (body: { name: string; address?: string; note?: string }) =>
    apiClient.post<WarehouseView>('/warehouses', body),
  deleteWarehouse: (id: string) => apiClient.del<{ success: true }>(`/warehouses/${id}`),

  /**
   * 地点列表（`GET /locations`，裸数组）。
   *
   * 实现委托给 `locationsApi.list`：同一个端点在两处各拼一次 URL，改路径时
   * 必然漏掉一处，而漏掉的那处只会在运行时 404。
   */
  listLocations: (params: { type?: LocationType; active?: boolean } = {}) =>
    locationsApi.list(params),

  /** 裸数组，非分页（契约 §9 第 1 行） */
  listStockItems: (params: { locationId?: string; productId?: string } = {}) =>
    apiClient.get<StockItemView[]>('/stock/items', { params }),

  summary: () => apiClient.get<StockSummaryRow[]>('/stock/summary'),

  adjust: (body: { locationId: string; productId: string; quantity: number; reason: string }) =>
    apiClient.post<StockAdjustResult>('/stock/adjust', body),

  /** 五字段分页包裹（契约 §9 第 5 行） */
  listTransfers: (params: { route?: StockTransferRoute; status?: TransferStatus } = {}) =>
    apiClient.get<Paginated<TransferView>>('/stock/transfers', { params }),

  createTransfer: (body: Record<string, unknown>) =>
    apiClient.post<TransferView>('/stock/transfers', body),
  shipTransfer: (id: string) => apiClient.post<TransferView>(`/stock/transfers/${id}/ship`),
  /** 不传 `lines` 表示「每行按发运数量全收」（契约 §9.3 与 `ReceiveTransferDto` 的约定） */
  receiveTransfer: (id: string, lines: Array<{ lineId: string; receivedQty: number }> = []) =>
    apiClient.post<TransferView>(`/stock/transfers/${id}/receive`, { lines }),
  cancelTransfer: (id: string) => apiClient.post<TransferView>(`/stock/transfers/${id}/cancel`),

  /**
   * 展会结算的库存去向（契约 §9.4）。
   *
   * **数量不由请求体给**：子单的每一行 = 该展会地点上 `(productId, ownerTenantId)` 的
   * `StockItem.availableQty` **现值**（服务端算）。因此这里只传「去向 + 目标」，
   * 前端多传任何数量字段都会被当作不存在的输入。
   *
   * 去向只能选一次：重复选**同一个**去向 ⇒ `200` + `replayed: true` + 同一个 `childTransferId`；
   * 选另一个 ⇒ `409 DESTINATION_ALREADY_SET`（文案 `该单据的去向已选定`）。
   */
  settleDestination: (
    id: string,
    body: { destination: TransferDestination; nextEventId?: string; warehouseId?: string },
  ) => apiClient.post<SettleDestinationResponse>(`/stock/transfers/${id}/settle-destination`, body),

  /** 展转凭证链（契约 §9.5）：**裸数组**，根在首位；查链上任何一环都得到同一条链 */
  chain: (id: string) => apiClient.get<TransferChainNode[]>(`/stock/transfers/${id}/chain`),
};
